import { Button, ButtonGroup, Toolbar, ToolbarItem, ToolbarSpacer } from "@progress/kendo-react-buttons";
import { useLocalization } from "@progress/kendo-react-intl";
import useChartStore from "../../../Zustand/chartStore";
import { enMessages } from "../../../messages/en-US";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAlignJustify, faArrowsUpDown, faBarsProgress, faChartLine } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { chartColumnClusteredIcon , chevronDownIcon, windowRestoreIcon} from '@progress/kendo-svg-icons';
import { MultiSelect, MultiSelectChangeEvent,   } from "@progress/kendo-react-dropdowns";
import * as React from 'react';
import { Checkbox, InputSuffix } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";

export interface ChartToolbarConfig {
  title: string | null;
  lastSeen: string | null;
  showAutoRange: boolean;
  showThresholds: boolean;
  showAggregatorOptions: boolean;
  showChartOptions: boolean;
  showSingleMultipleChart: boolean;    
  showTable?: boolean;
  showBothCharts?: boolean;
  showFilterDataSeries?: boolean;
  allDataSeries?: any[];
  onDataSeriesChange?:any;
  selectedDataSeries?: any[];
};

const aggregatorNone = 'none';
const aggregatorDaily = 'daily';

const dataItemKey = 'id';
const textField = 'text';

export const ChartToolbar: React.FC<ChartToolbarConfig> = (props) => {

  const localizationService = useLocalization();
  const {isAutoRange, setAutoRange} = useChartStore((state) => ({isAutoRange: state.isAutoRange, setAutoRange: state.setAutoRange}));
  const {viewType, setViewType} = useChartStore((state) => ({viewType: state.viewType, setViewType: state.setViewType}));
  const {aggregator, setAggregator} = useChartStore((state) => ({aggregator: state.aggregator, setAggregator: state.setAggregator}));
  const {isSinglePressureChart, setSinglePressureChart} = useChartStore((state) => ({isSinglePressureChart: state.isSinglePressureChart, setSinglePressureChart: state.setSinglePressureChart}));
  const {isThresholdsOn, setThresholdsOn} = useChartStore((state) => ({isThresholdsOn: state.isThresholdsOn, setThresholdsOn: state.setThresholdsOn}));

  const autoRangeButton = () => {
    return (
      <Button 
        title={localizationService.toLanguageString('custom.autoRange', enMessages.custom.autoRange)} 
        togglable={true} 
        selected={isAutoRange} 
        onClick={() => { setAutoRange(!isAutoRange)}}><FontAwesomeIcon 
        title={localizationService.toLanguageString('custom.autoRange', enMessages.custom.autoRange)} 
        icon={faArrowsUpDown}/>
      </Button>
    )
  }

  const thresholdButton = () => {
    return (
      <Button 
        title={localizationService.toLanguageString('custom.thresholds', enMessages.custom.thresholds)} 
        togglable={true} selected={isThresholdsOn} 
        onClick={() => setThresholdsOn(!isThresholdsOn)}><FontAwesomeIcon 
        title={localizationService.toLanguageString('custom.thresholds', enMessages.custom.thresholds)} 
        icon={faBarsProgress}
        />
      </Button>
    )
  }

  const value = props.selectedDataSeries || [];

  const onChange = (event: MultiSelectChangeEvent) => {
    if (props.onDataSeriesChange) {
      props.onDataSeriesChange(event.value);
    }
  };

  const renderItemWithCheckbox = (li: React.ReactElement, itemProps: any) => {
    const itemChildren = (
      <span>
        <Checkbox
          name={itemProps.dataItem.value}
          checked={itemProps.selected}
          onChange={() => {
            // Create a synthetic event that matches what the MultiSelect expects
            const syntheticEvent = {
              stopPropagation: () => {},
              preventDefault: () => {},
              target: { checked: !itemProps.selected }
            };
            itemProps.onClick(itemProps.index, syntheticEvent);
          }}
        >
          <Label className="k-checkbox-label">&nbsp;{itemProps.dataItem.text}</Label>
        </Checkbox>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  return(
    <div>
      <Toolbar>
        <ToolbarItem>
          <div>
            {props.title && <div className="text-base text-left" style={{fontWeight:'bold'}}>{props.title}</div>}
            {props.lastSeen !== null && <div>{localizationService.toLanguageString('custom.lastSync', enMessages.custom.lastSync)}: {props.lastSeen}</div>}
          </div>
        </ToolbarItem>
        <ToolbarSpacer />
          {props.showSingleMultipleChart && 
          <ToolbarItem>
            <ButtonGroup >
              <Button 
                title={localizationService.toLanguageString('custom.singleOrMultipleChart', enMessages.custom.singleOrMultipleChart)} 
                togglable={true} 
                selected={!isSinglePressureChart} 
                onClick={() => setSinglePressureChart(!isSinglePressureChart)} svgIcon={windowRestoreIcon}>
              </Button>
            </ButtonGroup >
          </ToolbarItem>
          }
          {props.showFilterDataSeries && (
            <ToolbarItem>
              <MultiSelect
                style={{ width: '165px' }}
                data={props.allDataSeries}
                value={value}
                onChange={onChange}
                placeholder="Data Series"
                textField={textField}
                dataItemKey={dataItemKey}
                itemRender={renderItemWithCheckbox}
                suffix={() => (
                  <InputSuffix orientation="horizontal">
                      <Button themeColor="base" fillMode={'flat'} rounded={null} svgIcon={chevronDownIcon} />
                  </InputSuffix>
                )}
                tags={value.length > 0 ? [{ text: `(${value.length}) Data Series`, data: value }] : []}
                className="multi-select-custom-display multi-select-custom-display-readonly dropdown-menu-expanded-200"
                popupSettings={{ className: 'my-multiselect-popup' }}
                autoClose={false}
              />
            </ToolbarItem>
          )}
          {(props.showAutoRange && props.showThresholds) ?
          <ToolbarItem>
            <ButtonGroup >
              {props.showAutoRange &&
               autoRangeButton()
              }
              {props.showThresholds &&
               thresholdButton()
              }
            </ButtonGroup >
          </ToolbarItem>
          :
          <ToolbarItem>
              {props.showAutoRange &&
                autoRangeButton()
              }
              {props.showThresholds &&
                thresholdButton()
              }
          </ToolbarItem>
          }
          {props.showAggregatorOptions &&
          <ToolbarItem>
            <ButtonGroup >
              <Button 
                togglable={true} 
                selected={aggregator === aggregatorNone} 
                onClick={() => {setAggregator(aggregatorNone)}}>{localizationService.toLanguageString('custom.none', enMessages.custom.none)}
              </Button>
              <Button 
                togglable={true} 
                selected={aggregator === aggregatorDaily} 
                onClick={() => {setAggregator(aggregatorDaily)}}>{localizationService.toLanguageString('custom.daily', enMessages.custom.daily)}
              </Button>
            </ButtonGroup >
          </ToolbarItem>
          }
          {(props.showChartOptions) &&
          <ToolbarItem>
            <ButtonGroup >
              {props.showBothCharts !== false &&
              <Tooltip position={'bottom'} anchorElement={'target'} content={() => localizationService.toLanguageString('custom.barChart', enMessages.custom.barChart)} openDelay={2}>
                <Button 
                  title={localizationService.toLanguageString('custom.barChart', enMessages.custom.barChart)} 
                  svgIcon={chartColumnClusteredIcon} 
                  togglable={true}  
                  selected={viewType === 'bar' || viewType === 'userBar'} 
                  onClick={() => {setViewType('userBar')}}
                />
              </Tooltip>
              }
              <Tooltip position={'bottom'} anchorElement={'target'} content={() => localizationService.toLanguageString('custom.lineChart', enMessages.custom.lineChart)} openDelay={2}>
                <Button 
                  title={localizationService.toLanguageString('custom.lineChart', enMessages.custom.lineChart)} 
                  togglable={true} 
                  selected={viewType === 'line' || viewType === 'userLine'} 
                  onClick={() => {setViewType('userLine')}}>
                    <FontAwesomeIcon title={localizationService.toLanguageString('custom.lineChart', enMessages.custom.lineChart)} icon={faChartLine}/>
                </Button>
              </Tooltip>
              {props.showTable &&
              <Tooltip position={'bottom'} anchorElement={'target'} content={() => 'Table'} openDelay={2}>
                <Button 
                  title={'Table'} 
                  togglable={true} 
                  selected={viewType === 'table'} 
                  onClick={() => setViewType('table')}>
                    <FontAwesomeIcon title={'Table'} icon={faAlignJustify}/>
                  </Button>
              </Tooltip>
              }
            </ButtonGroup >
        </ToolbarItem>
          }
      </Toolbar>
    </div>
  );
}